/**
 * Business Unit Service
 * The brands an account is split into, used to scope every other view.
 */

import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import { BusinessUnitAdapter } from '@/adapters';
import type {
  ApiResponse,
  BusinessUnit,
  BusinessUnitsByAccountResponseDto,
  BusinessUnitsListResponseDto
} from '@/types';

/** The brands of each account the user belongs to, keyed by account id. */
export type BusinessUnitsByAccount = Record<string, BusinessUnit[]>;

export const businessUnitService = {
  /**
   * The caller's own account only — the backend takes the account from the JWT and does
   * not accept one from the client, so there is nothing to pass here.
   */
  async list(): Promise<BusinessUnit[]> {
    const response = await api.get<ApiResponse<BusinessUnitsListResponseDto>>(API_ENDPOINTS.BUSINESS_UNITS.LIST);
    return BusinessUnitAdapter.fromApiList(response.data.data.businessUnits);
  },

  /** Every account the caller belongs to, so the switcher knows which ones have brands. */
  async listByAccount(): Promise<BusinessUnitsByAccount> {
    const response = await api.get<ApiResponse<BusinessUnitsByAccountResponseDto>>(API_ENDPOINTS.BUSINESS_UNITS.ALL);
    return Object.fromEntries(
      Object.entries(response.data.data.byAccount ?? {}).map(([accountId, units]) => [
        accountId,
        BusinessUnitAdapter.fromApiList(units)
      ])
    );
  }
};
